import {
  dbNamesOf,
  SqlDatabase,
  type ClassType,
  type CustomSqlFilterBuilder,
} from "remult";
import {
  buildJsonbArrayNoneContainmentSql,
  buildJsonbArraySomeContainmentSql,
} from "./jsonb-array-containment";
import { type RelationalOrJsonEntityFilter } from "./sql-relations";

export class JsonSqlFilterBridge {
  private where = "";
  _addWhere = true;
  promises: Promise<void>[] = [];

  constructor(public readonly queryBuild: CustomSqlFilterBuilder) {}

  async resolveWhere() {
    while (this.promises.length > 0) {
      const p = this.promises;
      this.promises = [];
      for (const pr of p) {
        await pr;
      }
    }
    return this.where;
  }

  private add(data: {
    col: string;
    val: any;
    operator: string;
    not?: boolean;
    _disableBuildParam?: boolean;
  }) {
    this.promises.push(
      (async () => {
        const {
          col,
          operator,
          val,
          not = false,
          _disableBuildParam = false,
        } = data;

        const where = `${col} ${operator} ${_disableBuildParam || val === null ? val : this.queryBuild.param(val)}`;
        this.addToWhere(not ? `NOT (${where})` : where);
      })(),
    );
  }

  addToWhere(x: string) {
    if (this.where.length == 0) {
      if (this._addWhere) this.where += " WHERE ";
      this._addWhere = false;
    } else this.where += " AND ";
    this.where += x;
  }

  private arrayToList(arr: any[]) {
    return `(${arr.map((v) => this.queryBuild.param(v)).join(",")})`;
  }

  private castTo(col: string, val: any) {
    switch (typeof val) {
      case "string":
        return `regexp_replace((${col})::text, '^"(.+)"$', '\\1', 'g' /* Removing first and last " to parse a JSON string */)`; // Replace first " and last " that comes from the stringified string
      case "number":
      case "bigint":
        return `(${col})::float`;
      case "boolean":
        return `(${col})::boolean`;
      case "object":
        if (val === null) return col; // Don't cast null values
        return `(${col})::jsonb`; // For arrays & objects
      default:
        return col; // Fallback to not casting
    }
  }

  public arrayToPath(arr: string[]) {
    const [starter, ...rest] = arr;
    return function (to: "value" | "elm") {
      return rest.length === 0
        ? `"${starter}"`
        : `"${starter}"${to === "elm" ? "#>" : "#>>"}'{${rest.join()}}'`;
    };
  }

  and(andElements: JsonSqlFilterBridge[]) {
    this.promises.push(
      (async () => {
        const andWhere: string[] = [];
        for (const elm of andElements) {
          const where = await elm.resolveWhere();
          if (where) andWhere.push(where);
        }

        if (andWhere.length === 1) this.addToWhere(andWhere[0]);
        else if (andWhere.length > 1)
          this.addToWhere(`(${andWhere.join(" AND ")})`);
      })(),
    );
  }

  or(orElements: JsonSqlFilterBridge[]) {
    this.promises.push(
      (async () => {
        const orWhere: string[] = [];
        for (const elm of orElements) {
          const where = await elm.resolveWhere();
          if (where) orWhere.push(where);
        }
        if (orWhere.length === 1) this.addToWhere(orWhere[0]);
        else if (orWhere.length > 1)
          this.addToWhere(`(${orWhere.join(" OR ")})`);
      })(),
    );
  }

  not(element: JsonSqlFilterBridge) {
    this.promises.push(
      (async () => {
        const notWhere = await element.resolveWhere();
        if (notWhere) this.addToWhere(`NOT (${notWhere})`);
      })(),
    );
  }

  is(element: JsonSqlFilterBridge) {
    this.promises.push(
      (async () => {
        const where = await element.resolveWhere();
        this.addToWhere(where);
      })(),
    );
  }

  isNull(col: string): void {
    this.add({
      col,
      operator: "IS",
      val: null,
    });
  }

  isNotNull(col: string): void {
    this.add({
      col,
      operator: "IS NOT",
      val: null,
    });
  }

  isIn(col: string, val: any[]): void {
    if (val.length === 0)
      return this.add({
        col: "1",
        operator: "=",
        val: "0 /*isIn with no values*/",
      });
    this.add({
      col: this.castTo(col, val[0]),
      operator: "IN",
      val: this.arrayToList(val),
      _disableBuildParam: true,
    });
  }

  isNotIn(col: string, val: any[]): void {
    if (val.length === 0)
      return this.add({
        col: "1",
        operator: "=",
        val: "1 /*isNotIn with no values*/",
      });
    this.add({
      not: true,
      col: this.castTo(col, val[0]),
      operator: "IN",
      val: this.arrayToList(val),
      _disableBuildParam: true,
    });
  }

  isEqualTo(col: string, val: any): void {
    if (val === null) {
      return this.isNull(col);
    }
    this.add({
      col: this.castTo(col, val),
      operator: "=",
      val,
    });
  }

  isDifferentFrom(col: string, val: any): void {
    if (val === null) {
      return this.isNotNull(col);
    }
    this.add({
      col: this.castTo(col, val),
      operator: "<>",
      val,
    });
  }

  isGreaterOrEqualTo(col: string, val: number): void {
    this.add({
      col: this.castTo(col, val),
      operator: ">=",
      val,
    });
  }

  isGreaterThan(col: string, val: number): void {
    this.add({
      col: this.castTo(col, val),
      operator: ">",
      val,
    });
  }

  isLessOrEqualTo(col: string, val: number): void {
    this.add({
      col: this.castTo(col, val),
      operator: "<=",
      val,
    });
  }

  isLessThan(col: string, val: number): void {
    this.add({
      col: this.castTo(col, val),
      operator: "<",
      val,
    });
  }

  containsCaseInsensitive(col: string, val: string): void {
    this.add({
      col: `lower (${this.castTo(col, val)})`,
      operator: "like",
      val: `lower ('%' || ${this.queryBuild.param(val)} || '%')`,
      _disableBuildParam: true,
    });
  }

  notContainsCaseInsensitive(col: string, val: string): void {
    this.add({
      not: true,
      col: `lower (${this.castTo(col, val)})`,
      operator: "like",
      // הוספת || בין האחוזים לפרמטר
      val: `lower ('%' || ${this.queryBuild.param(val)} || '%')`,
      _disableBuildParam: true,
    });
  }

  startsWithCaseInsensitive(col: string, val: string): void {
    this.add({
      col: `lower (${this.castTo(col, val)})`,
      operator: "like",
      val: `lower (${this.queryBuild.param(val)} || '%')`,
      _disableBuildParam: true,
    });
  }

  endsWithCaseInsensitive(col: string, val: any): void {
    this.add({
      col: `lower (${this.castTo(col, val)})`,
      operator: "like",
      val: `lower ('%' || ${this.queryBuild.param(val)})`,
      _disableBuildParam: true,
    });
  }

  keyExist(col: string, key: string): void {
    this.add({
      col,
      operator: "?",
      val: key,
    });
  }
}

export function jsonFilterToEntityFilter<T>(
  filter: RelationalOrJsonEntityFilter<T>,
  parent: ClassType<any>,
  colWhiteList: string[],
  addWhere = false,
) {
  if (!Object.keys(filter).length) return {};

  return SqlDatabase.rawFilter(async (build) => {
    const dbNames = await dbNamesOf(parent);

    const parsedFilter: Record<any, any> = {};
    Object.keys(filter).forEach((key) => {
      if (dbNames[key]) {
        parsedFilter[dbNames[key].replaceAll('"', "")] =
          filter[key as keyof typeof filter];
      } else {
        parsedFilter[key] = filter[key as keyof typeof filter];
      }
    });

    const entityName = dbNames.$entityName.slice(1, -1);

    const whiteList = [
      ...(Object.values(dbNames)
        .map((v) => (typeof v === "string" ? v.slice(1, -1) : undefined))
        .filter(Boolean) as string[]),
      ...colWhiteList,
    ];

    ensureWhiteList(parsedFilter, whiteList);

    const bridge = await jsonFilterToJsonSqlFilterBridge(
      parsedFilter,
      build,
      [],
      addWhere,
      entityName,
    );
    build.sql = await bridge.resolveWhere();
    return build.sql;
  });
}

async function jsonFilterToJsonSqlFilterBridge<T extends Record<string, any>>(
  filter: RelationalOrJsonEntityFilter<T>,
  build: CustomSqlFilterBuilder,
  prePath: PropertyKey[] = [],
  _addWhere = false,
  base?: string,
) {
  const bridge = new JsonSqlFilterBridge(build);
  bridge._addWhere = _addWhere;

  if (filter === null || typeof filter !== "object") {
    // If filter is not an object but a value
    await resolveOperatorKey(
      prePath.map((v) => v.toString()),
      filter,
      filter,
      filter,
      bridge,
    );
  } else
    for (const key in filter) {
      switch (key) {
        case "$and":
          bridge.and(
            await Promise.all(
              (filter[key] ?? []).map((f) =>
                jsonFilterToJsonSqlFilterBridge(f, build, prePath, false, base),
              ),
            ),
          );
          break;
        case "$or":
          bridge.or(
            await Promise.all(
              (filter[key] ?? []).map((f) =>
                jsonFilterToJsonSqlFilterBridge(f, build, prePath, false, base),
              ),
            ),
          );
          break;
        case "$not":
          if (filter[key] !== null && typeof filter[key] === "object") {
            bridge.not(
              await jsonFilterToJsonSqlFilterBridge(
                filter[key] ?? {},
                build,
                prePath,
                false,
                base,
              ),
            );
          } else {
            await resolveOperatorKey(
              prePath.map((v) => v.toString()),
              key,
              key,
              filter[key as keyof typeof filter],
              bridge,
            );
          }
          break;
        default:
          if (ALLOWED_OPERATORS.includes(key)) {
            // Meaning this is a native value filter (i.e: a filter for a string value and not for a field)
            await resolveOperatorKey(
              prePath.map((v) => v.toString()),
              key,
              key,
              filter[key as keyof typeof filter],
              bridge,
            );
          } else {
            // Meaning this is a value filter
            const concatFieldName = [
              ...prePath,
              base ? `${base}"."${key}` : key,
            ].map((v) => v.toString());
            const fieldFilter = filter[key as keyof typeof filter];

            if (!fieldFilter) {
              console.log("No field filter for: ", key);
              continue;
            }

            for (const [operator, entry] of Object.entries(fieldFilter)) {
              await resolveOperatorKey(
                concatFieldName,
                key,
                operator,
                entry,
                bridge,
              );
            }
          }
      }
    }

  return bridge;
}

async function resolveOperatorKey(
  concatFieldName: string[],
  fieldKey: string,
  operator: string,
  value: any,
  bridge: JsonSqlFilterBridge,
) {
  const fieldName = bridge.arrayToPath(concatFieldName);

  const asKey = `${fieldKey}_instance`;

  const tryBuildJsonbArrayContainmentWhere = (): string | undefined => {
    // Optimization for JSONB arrays of primitive strings (e.g. association.roles):
    // use Postgres jsonb containment instead of jsonb_array_elements + regexp_replace.
    // Exact enum membership only - see buildJsonbArraySomeContainmentSql.
    const colExpr = `(${fieldName("elm")})::jsonb`;
    const paramText = (x: string) => `${bridge.queryBuild.param(x)}::text`;
    return buildJsonbArraySomeContainmentSql(colExpr, value, paramText);
  };

  const tryBuildJsonbArrayNoneContainmentWhere = (): string | undefined => {
    const colExpr = `(${fieldName("elm")})::jsonb`;
    const paramText = (x: string) => `${bridge.queryBuild.param(x)}::text`;
    return buildJsonbArrayNoneContainmentSql(colExpr, value, paramText);
  };

  switch (operator) {
    case "$every":
      bridge.addToWhere(`not exists (
        select 1 from jsonb_array_elements (COALESCE(${fieldName("elm")}, '[]')::jsonb) as ${asKey}
        ${await (await jsonFilterToJsonSqlFilterBridge({ $not: value }, bridge.queryBuild, [asKey], true)).resolveWhere()}
        )`);
      break;
    case "$some":
      // Fast-path for JSONB arrays of strings: col @> jsonb_build_array(...)
      {
        const optimized = tryBuildJsonbArrayContainmentWhere();
        if (optimized) {
          bridge.addToWhere(optimized);
          break;
        }
      }

      bridge.addToWhere(`exists (
        select 1 from jsonb_array_elements (COALESCE(${fieldName("elm")}, '[]')::jsonb) as ${asKey}
        ${await (await jsonFilterToJsonSqlFilterBridge(value, bridge.queryBuild, [asKey], true)).resolveWhere()}
        )`);
      break;
    case "$none":
      {
        const optimizedNone = tryBuildJsonbArrayNoneContainmentWhere();
        if (optimizedNone) {
          bridge.addToWhere(optimizedNone);
          break;
        }
      }
      bridge.addToWhere(`not exists (
        select 1 from jsonb_array_elements (COALESCE(${fieldName("elm")}, '[]')::jsonb) as ${asKey}
        ${await (await jsonFilterToJsonSqlFilterBridge(value, bridge.queryBuild, [asKey], true)).resolveWhere()}
        )`);
      break;
    case "$is":
      bridge.is(
        await jsonFilterToJsonSqlFilterBridge(
          value,
          bridge.queryBuild,
          concatFieldName,
        ),
      );
      break;
    case "$isNot":
      bridge.not(
        await jsonFilterToJsonSqlFilterBridge(
          value,
          bridge.queryBuild,
          concatFieldName,
        ),
      );
      break;
    case "$gte":
    case ">=":
      bridge.isGreaterOrEqualTo(fieldName("value"), value);
      break;
    case "$gt":
    case ">":
      bridge.isGreaterThan(fieldName("value"), value);
      break;
    case "$lte":
    case "<=":
      bridge.isLessOrEqualTo(fieldName("value"), value);
      break;
    case "$lt":
    case "<":
      bridge.isLessThan(fieldName("value"), value);
      break;
    case "$ne":
    case "$not":
    case "!=":
    case "$nin":
      if (Array.isArray(value)) {
        bridge.isNotIn(fieldName("value"), value);
      } else if (value !== null && typeof value === "object")
        bridge.not(
          await jsonFilterToJsonSqlFilterBridge(
            value,
            bridge.queryBuild,
            concatFieldName,
          ),
        );
      else bridge.isDifferentFrom(fieldName("value"), value);
      break;
    case "$in":
      bridge.isIn(fieldName("value"), value);
      break;
    case "=":
      bridge.isEqualTo(fieldName("value"), value);
      break;
    case "$contains":
      bridge.containsCaseInsensitive(fieldName("value"), value);
      break;
    case "$startsWith":
      bridge.startsWithCaseInsensitive(fieldName("value"), value);
      break;
    case "$endsWith":
      bridge.endsWithCaseInsensitive(fieldName("value"), value);
      break;
    case "$notContains":
      bridge.notContainsCaseInsensitive(fieldName("value"), value);
      break;
    default:
      bridge.isEqualTo(fieldName("value"), value);
  }
}

const ALLOWED_OPERATORS = [
  "$and",
  "$or",
  "$not",
  "$is",
  "$isNot",
  "$gte",
  "$gt",
  "$lte",
  "$lt",
  "$ne",
  "$nin",
  "$in",
  "$contains",
  "$startsWith",
  "$endsWith",
  "$notContains",
  "$every",
  "$some",
  "$none",
  "!=",
  ">=",
  "<=",
  ">",
  "<",
  "=",
];

function ensureWhiteList(
  filter: RelationalOrJsonEntityFilter<any>,
  whiteList: string[],
) {
  if (!filter) return;

  Object.keys(filter).forEach((key) => {
    if (![...whiteList, ...ALLOWED_OPERATORS].includes(key)) {
      if (key === "$id") {
        throw new Error(
          `\`$id\` is not a valid operator for JSON fields, please use \`{ $is: { $id: ... } }\``,
        );
      } else
        throw new Error(`SECURITY ALERT: field ${key} is not in white list`);
    }

    if (typeof filter[key] === "object" && filter[key] !== null) {
      if (Array.isArray(filter[key])) {
        filter[key].forEach((k: any) => ensureWhiteList(k, whiteList));
      } else {
        ensureWhiteList(filter[key], whiteList);
      }
    }
  });
}
